import dayjs from "dayjs";
import type { UseQueryResult } from "@tanstack/react-query";
import type { PriceInfo } from "~/domains/pricing/api/PriceInfo";
import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { StockLevel } from "~/domains/product/components/StockIndicator/StockIndicator";
import { text as formatPrice } from "~/utils/PriceUtils";
import type { ConfiguratorSummaryActionId } from "./summaryCapabilities";

export type SummaryPriceViewModel =
  | { state: "hidden" }
  | { state: "loading" }
  | { state: "error" }
  | { state: "onRequest" }
  | { state: "value"; text: string };

export type SummaryAvailabilityViewModel =
  | { state: "hidden" }
  | { state: "loading" }
  | { state: "error" }
  | { state: "value"; text?: string; level?: StockLevel; leadTimeWeeks?: number };

const getPriceForQuantity = (prices: readonly PriceInfo[], quantity: number) => {
  const price = prices[0];
  if (!price?.hasPrice) {
    return null;
  }
  return [...price.priceScale]
    .sort((left, right) => right.minQty - left.minQty)
    .find((entry) => entry.minQty <= quantity)?.price;
};

export const buildSummaryPriceViewModel = (
  visible: boolean,
  query: UseQueryResult<PriceInfo[], Error>,
  quantity: number,
): SummaryPriceViewModel => {
  if (!visible) {
    return { state: "hidden" };
  }
  if (query.isPending || query.isFetching) {
    return { state: "loading" };
  }
  if (query.isError) {
    return { state: "error" };
  }
  const price = getPriceForQuantity(query.data ?? [], quantity);
  return price?.value ? { state: "value", text: formatPrice(price) } : { state: "onRequest" };
};

export const buildSummaryAvailabilityViewModel = (
  visible: boolean,
  query: UseQueryResult<AvailabilityInfo, Error>,
): SummaryAvailabilityViewModel => {
  if (!visible) {
    return { state: "hidden" };
  }
  if (query.isPending || query.isFetching) {
    return { state: "loading" };
  }
  if (query.isError) {
    return { state: "error" };
  }
  const packaging = query.data?.packagingInformation[0];
  if (!packaging) {
    return { state: "value", level: StockLevel.NOT_IN_STOCK };
  }
  const leadTime = packaging.replenishmentLeadTimeDate;
  if (leadTime) {
    const leadTimeWeeks = Math.ceil(dayjs(leadTime).diff(dayjs(), "day") / 7);
    if (leadTimeWeeks > 0) {
      return { state: "value", text: "", level: StockLevel.NOT_IN_STOCK, leadTimeWeeks };
    }
  }
  const inStock = packaging.totalWarehouse.inStock.reduce((total, entry) => total + entry.quantity, 0) > 0;
  return {
    state: "value",
    level: inStock ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK,
  };
};

export interface SummaryCommerceViewModel {
  quantity: number;
  showQuantity: boolean;
  quantitySettling: boolean;
  priceReference: string;
  priceReferences: readonly { label: string; value: string }[];
  showPriceReference: boolean;
  priceReferenceEditable: boolean;
  price: SummaryPriceViewModel;
  availability: SummaryAvailabilityViewModel;
  actions: readonly SummaryActionViewModel[];
  contactSpecialistUrl?: string;
  showCad: boolean;
  actionError?: string;
}

export interface SummaryActionViewModel {
  id: ConfiguratorSummaryActionId;
  visible: boolean;
  enabled: boolean;
  variant: "contained" | "outlined" | "text";
  color: "primary" | "secondary";
}
